import React, { useEffect, useRef, useState } from 'react';
import BasicCli from '../../main/components/basicCli/BasicCli';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import DnsIcon from '@mui/icons-material/Dns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  Tooltip as TT,
  Legend as LG,
  ArcElement,
} from 'chart.js';
import { ApiConstants } from '../../main/constants/ApiConstants';
import './Dashboard.css';
import { toast } from 'react-toastify';
import axiosInstance from '../../main/initializers/axios';
import IDashboardCard from '../../main/interfaces/IDashboardCard';
import IGraphData from '../../main/interfaces/IDashboardGraph';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import IPagination from '../../main/interfaces/IPagination';
import { Pie } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import GeneralModal from '../../main/components/modal/general/GeneralModal';
import WeatherCard from '../../components/dashboard/WeatherCard';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, TT, LG, Filler, ArcElement);

interface CashBalanceRow {
  id: number;
  companyId: number;
  integrationId: number;
  value: number;
  created: string;
  deleted?: string | null;
}

interface CashBalanceListResponse {
  hasBalanceForToday: boolean;
  todayValue?: number | null;
  items: CashBalanceRow[];
}

interface CompanyLocation {
  id?: number | null;
  name?: string | null;
  lat?: number | string | null;
  lng?: number | string | null;
  Lat?: number | string | null;
  Lng?: number | string | null;
  Name?: string | null;
}

interface DashboardCompany {
  location?: CompanyLocation | null;
  Location?: CompanyLocation | null;
  locationName?: string | null;
  LocationName?: string | null;
}

interface ServerHealth {
  status: 'Healthy' | 'Degraded' | 'Busy' | 'Offline';
  cpuUsagePercent: number;
  memoryUsagePercent: number;
  threadPoolUsagePercent: number;
  uptimeSeconds: number;
  checkedAtUtc: string;
}

const toFiniteNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const Dashboard: React.FC = () => {
  const { i18n } = useTranslation();

  const [card, setCards] = useState<IDashboardCard>();
  const [graphs, setGraph] = useState<IGraphData>();
  const [restaurantData, setRestaurantData] = useState<any[]>([]);
  const [animatedRestaurantData, setAnimatedRestaurantData] = useState<any[]>([]);
  const [barData, setBarData] = useState<any[]>([]);
  const [animatedBarData, setAnimatedBarData] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [selectedItem, setSelectedItem] = useState({});
  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });
  const [pieChartData, setPieChartData] = useState<{ category: string; total: number; color: string; percentage: number }[]>([]);
  const [trendValue, setTrendValue] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [isTogglingFiscal, setIsTogglingFiscal] = useState(false);
  const [fiscalBorderFlash, setFiscalBorderFlash] = useState<'none' | 'green' | 'red'>('none');
  const [cashBalanceRows, setCashBalanceRows] = useState<CashBalanceRow[]>([]);
  const [hasCashBalanceForToday, setHasCashBalanceForToday] = useState(false);
  const [todayCashBalanceValue, setTodayCashBalanceValue] = useState<number | null>(null);
  const [isCashBalanceModalOpen, setIsCashBalanceModalOpen] = useState(false);
  const [cashBalanceValue, setCashBalanceValue] = useState('');
  const [isSavingCashBalance, setIsSavingCashBalance] = useState(false);
  const [company, setCompany] = useState<DashboardCompany | null>(null);
  const [serverHealth, setServerHealth] = useState<ServerHealth | null>(null);
  const holdStartRef = useRef<number>(0);
  const holdTimerRef = useRef<number | null>(null);
  const holdTokenRef = useRef(0);
  const borderFlashTimerRef = useRef<number | null>(null);
  const HOLD_MS = 5000;
  const restaurantTotal = restaurantData.reduce((sum, item: any) => sum + item.orders, 0);
  const barTotal = barData.reduce((sum, item: any) => sum + item.orders, 0);
  const cashBalanceCardClass = hasCashBalanceForToday ? 'cash-balance-ready' : 'cash-balance-missing';
  const companyLocation = company?.location ?? company?.Location ?? null;
  const companyLatitude = toFiniteNumber(companyLocation?.lat ?? companyLocation?.Lat);
  const companyLongitude = toFiniteNumber(companyLocation?.lng ?? companyLocation?.Lng);
  const companyLocationName =
    companyLocation?.name ??
    companyLocation?.Name ??
    company?.locationName ??
    company?.LocationName ??
    undefined;

  const chartOptionsPie = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'right' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 12,
            family: 'Segoe UI, Roboto, sans-serif',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#2c3e50',
        bodyColor: '#34495e',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        displayColors: true,
        callbacks: {},
      },
    },
    cutout: '55%',
    animation: {
      animateRotate: true,
      duration: 1400,
    },
  };

  const selectedItemPagination = (item: IPagination) => {
    // nëse do ta përdorësh më vonë, e ke gati
  };

  const fetchData = async () => {
    try {
      const cardData = await axiosInstance.get(ApiConstants.Dashboard.cards);
      setCards(cardData.data);
    } catch {
      toast.error(i18n.t('errors.fetchDashboardCards'));
    }

    try {
      const graphData = await axiosInstance.get(ApiConstants.Dashboard.graphs);
      const dataFormatted = graphData.data as IGraphData;

      // Restaurant Data
      const resArray: any[] = [];
      if (dataFormatted.soldRestaurant.labels.length > 0) {
        let index = 0;
        for (const item of dataFormatted.soldRestaurant.labels) {
          resArray.push({
            month: item,
            orders: dataFormatted.soldRestaurant.counters[index],
          });
          index++;
        }
      }
      setRestaurantData(resArray);
      setAnimatedRestaurantData(resArray);

      // Bar Data
      const barArray: any[] = [];
      if (dataFormatted.soldBar.labels.length > 0) {
        let index = 0;
        for (const item of dataFormatted.soldBar.labels) {
          barArray.push({
            month: item,
            orders: dataFormatted.soldBar.counters[index],
          });
          index++;
        }
      }
      setBarData(barArray);
      setAnimatedBarData(barArray);

      // Totale për Pie Chart
      const totalRestaurantOrders = dataFormatted.soldRestaurant.counters.reduce((sum, count) => sum + count, 0);
      const totalBarOrders = dataFormatted.soldBar.counters.reduce((sum, count) => sum + count, 0);
      const total = totalRestaurantOrders + totalBarOrders;
      setTotalOrders(total);

      setGraph(graphData.data);

      // Trend calculations
      const lastRestaurant = dataFormatted.soldRestaurant.counters.at(-1) || 0;
      const prevRestaurant = dataFormatted.soldRestaurant.counters.at(-2) || 0;
      const lastBar = dataFormatted.soldBar.counters.at(-1) || 0;
      const prevBar = dataFormatted.soldBar.counters.at(-2) || 0;
      const totalLastMonth = lastRestaurant + lastBar;
      const totalPrevMonth = prevRestaurant + prevBar;
      const trend = totalPrevMonth === 0 ? 0 : ((totalLastMonth - totalPrevMonth) / totalPrevMonth) * 100;
      setTrendValue(trend);

      // Pie data (me përkthime)
      const pieData = [
        {
          category: i18n.t('generic.restaurantOrders'),
          total: totalRestaurantOrders,
          color: '#ff6384',
          percentage: total === 0 ? 0 : Math.round((totalRestaurantOrders / total) * 100),
        },
        {
          category: i18n.t('generic.barOrders'),
          total: totalBarOrders,
          color: '#36a2eb',
          percentage: total === 0 ? 0 : Math.round((totalBarOrders / total) * 100),
        },
      ];

      setPieChartData(pieData);
    } catch {
      toast.error(i18n.t('errors.fetchDashboardGraphs'));
    }

    try {
      const ordersRes = await axiosInstance.get(ApiConstants.Dashboard.lastDayOrder);
      setOrders(ordersRes.data);
    } catch {
      toast.error(i18n.t('errors.fetchDashboardOrders'));
    }

    try {
      const cashBalanceRes = await axiosInstance.get<CashBalanceListResponse>(ApiConstants.Dashboard.cashBalanceList);
      setCashBalanceRows(cashBalanceRes.data?.items ?? []);
      setHasCashBalanceForToday(!!cashBalanceRes.data?.hasBalanceForToday);
      setTodayCashBalanceValue(cashBalanceRes.data?.todayValue ?? null);
    } catch {
      toast.error(i18n.t('dashboard.cashBalance.fetchError', { defaultValue: 'Unable to load cash balance data.' }));
    }

    try {
      const companyRes = await axiosInstance.get<DashboardCompany>('api/company');
      setCompany(companyRes.data);
    } catch {
      setCompany(null);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isMounted = true;
    let requestInProgress = false;

    const loadServerHealth = async () => {
      if (requestInProgress) return;

      try {
        requestInProgress = true;
        const response = await axiosInstance.get<ServerHealth>(
          ApiConstants.Dashboard.serverHealth,
          { skipLoader: true, skipErrorToast: true } as any
        );
        if (isMounted) {
          setServerHealth(response.data);
        }
      } catch {
        if (isMounted) {
          setServerHealth((previous) => ({
            status: 'Offline',
            cpuUsagePercent: previous?.cpuUsagePercent ?? 0,
            memoryUsagePercent: previous?.memoryUsagePercent ?? 0,
            threadPoolUsagePercent: previous?.threadPoolUsagePercent ?? 0,
            uptimeSeconds: previous?.uptimeSeconds ?? 0,
            checkedAtUtc: previous?.checkedAtUtc ?? new Date().toISOString(),
          }));
        }
      } finally {
        requestInProgress = false;
      }
    };

    void loadServerHealth();
    const intervalId = window.setInterval(loadServerHealth, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!isCashBalanceModalOpen) {
      setCashBalanceValue('');
    }
  }, [isCashBalanceModalOpen]);

  useEffect(() => {
    if (animatedRestaurantData.length <= 1) return;

    const intervalId = setInterval(() => {
      setAnimatedRestaurantData((prev) => {
        if (prev.length <= 1) return prev;
        const lastItem = prev[prev.length - 1];
        return [lastItem, ...prev.slice(0, prev.length - 1)];
      });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [animatedRestaurantData.length]);

  useEffect(() => {
    if (animatedBarData.length <= 1) return;

    const intervalId = setInterval(() => {
      setAnimatedBarData((prev) => {
        if (prev.length <= 1) return prev;
        const lastItem = prev[prev.length - 1];
        return [lastItem, ...prev.slice(0, prev.length - 1)];
      });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [animatedBarData.length]);

  const getTrendIcon = () => {
    if (trendValue > 0) return <TrendingUpIcon className="trend-icon up" />;
    if (trendValue < 0) return <TrendingDownIcon className="trend-icon down" />;
    return <TrendingFlatIcon className="trend-icon neutral" />;
  };

  const getTrendClass = () => {
    if (trendValue > 0) return 'is-up';
    if (trendValue < 0) return 'is-down';
    return 'is-neutral';
  };

  const stopFiscalHold = () => {
    holdTokenRef.current += 1;
    if (holdTimerRef.current) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const submitToggleCanPrintFiscal = async () => {
    if (isTogglingFiscal) return;
    try {
      setIsTogglingFiscal(true);
      const res = await axiosInstance.post(ApiConstants.User.toggleCanPrintFiscal, {
        holdMilliseconds: HOLD_MS,
      });
      const nextValue = !!res?.data?.canPrintFiscal;
      setFiscalBorderFlash(nextValue ? 'green' : 'red');
    } catch {
      setFiscalBorderFlash('red');
      toast.error(i18n.t('orderDetails.somethingWentWrong'));
    } finally {
      if (borderFlashTimerRef.current) {
        window.clearTimeout(borderFlashTimerRef.current);
      }
      borderFlashTimerRef.current = window.setTimeout(() => setFiscalBorderFlash('none'), 1000);
      setIsTogglingFiscal(false);
      stopFiscalHold();
    }
  };

  const startFiscalHold = () => {
    if (isTogglingFiscal) return;
    holdTokenRef.current += 1;
    const token = holdTokenRef.current;
    holdStartRef.current = Date.now();
    holdTimerRef.current = window.setTimeout(() => {
      if (holdTokenRef.current === token) {
        void submitToggleCanPrintFiscal();
      }
    }, HOLD_MS + 10);
  };

  const openCashBalanceModal = () => {
    if (hasCashBalanceForToday) {
      toast.error(i18n.t('dashboard.cashBalance.onlyOncePerDay', { defaultValue: 'Cash balance can be registered only one time per day.' }));
      return;
    }

    setCashBalanceValue(todayCashBalanceValue != null ? String(todayCashBalanceValue) : '');
    setIsCashBalanceModalOpen(true);
  };

  const saveCashBalance = async () => {
    if (hasCashBalanceForToday) {
      toast.error(i18n.t('dashboard.cashBalance.onlyOncePerDay', { defaultValue: 'Cash balance can be registered only one time per day.' }));
      setIsCashBalanceModalOpen(false);
      return;
    }

    const parsedValue = Number(cashBalanceValue);

    if (!cashBalanceValue.trim() || Number.isNaN(parsedValue) || parsedValue < 0) {
      toast.error(i18n.t('dashboard.cashBalance.invalidValue', { defaultValue: 'Please enter a valid cash balance value.' }));
      return;
    }

    try {
      setIsSavingCashBalance(true);
      await axiosInstance.post(ApiConstants.Dashboard.registerCashBalance, { value: parsedValue });
      toast.success(i18n.t('dashboard.cashBalance.saved', { defaultValue: 'Cash balance registered successfully.' }));
      setIsCashBalanceModalOpen(false);
      await fetchData();
    } catch {
      // handled by interceptor
    } finally {
      setIsSavingCashBalance(false);
    }
  };

  useEffect(() => {
    return () => {
      if (holdTimerRef.current) {
        window.clearTimeout(holdTimerRef.current);
      }
      if (borderFlashTimerRef.current) {
        window.clearTimeout(borderFlashTimerRef.current);
      }
    };
  }, []);

  return (
    <BasicCli>
      <div className="dashboard">
        {/* HEADER */}
        <div className="dashboard-header">
          <h1>{i18n.t('dashboard.overviewTitle')}</h1>

          <div
            className={`trend-indicator ${getTrendClass()} fiscal-flash-${fiscalBorderFlash}`}
            onMouseDown={startFiscalHold}
            onMouseUp={stopFiscalHold}
            onMouseLeave={stopFiscalHold}
            onTouchStart={(e) => {
              e.preventDefault();
              startFiscalHold();
            }}
            onTouchEnd={stopFiscalHold}
            onTouchCancel={stopFiscalHold}
            aria-disabled={isTogglingFiscal}
          >
            {getTrendIcon()}
            <span className="trend-text">
              {trendValue >= 0 ? '+' : ''}
              {trendValue.toFixed(1)}% {i18n.t('dashboard.vsLastMonth')}
            </span>
          </div>
        </div>

        <div className="dashboard-top-grid">
          <div className="dashboard-summary-card">
            <div className="summary-card-header">
              <div>
                <span className="summary-eyebrow"></span>
                <h2>{i18n.t('dashboard.performanceOverview', { year: new Date().getFullYear() })}</h2>
              </div>
              <span className={`summary-health-pill ${hasCashBalanceForToday ? 'success' : 'warning'}`}>
                {hasCashBalanceForToday
                  ? i18n.t('dashboard.cashBalance.ready', { defaultValue: 'Ready' })
                  : i18n.t('dashboard.cashBalance.missing', { defaultValue: 'Action needed' })}
              </span>
            </div>

            {/* TOP CARDS */}
            <div className="cards summary-metrics-grid">
            <div
              className={`card_dashboard summary-metric-card cash-balance-card ${cashBalanceCardClass} ${hasCashBalanceForToday ? 'cash-balance-card-disabled' : ''}`}
              onClick={openCashBalanceModal}
              role="button"
              tabIndex={0}
            >
              <div className="card-icon-wrapper cash-balance-icon-wrapper">
                <InventoryIcon fontSize="large" className="card-icon" />
              </div>
              <h3>{i18n.t('dashboard.cashBalance.title', { defaultValue: 'Cashier Opening Balance' })}</h3>
              <p className="card-main-value">
                {todayCashBalanceValue != null ? todayCashBalanceValue.toFixed(2) : i18n.t('dashboard.cashBalance.notStarted', { defaultValue: 'Not set' })}
              </p>
              <div className="card-subtext">
                <span className={`status-badge ${hasCashBalanceForToday ? 'success' : 'warning'}`}>
                  {hasCashBalanceForToday
                    ? i18n.t('dashboard.cashBalance.ready', { defaultValue: 'Registered today' })
                    : i18n.t('dashboard.cashBalance.missing', { defaultValue: 'Missing today' })}
                </span>
              </div>
            </div>

            {/* Card 1 */}
            <div className="card_dashboard summary-metric-card">
              <div className="card-icon-wrapper">
                <InventoryIcon fontSize="large" className="card-icon" />
              </div>
              <h3>{i18n.t('generic.totalProducts')}</h3>
              <p className="card-main-value">{card?.productMinStock?.total || 0}</p>
              <div className="card-subtext">
                <span className={`status-badge ${(card?.productMinStock?.totalBelowMin || 0) > 0 ? 'warning' : 'success'}`}>
                  {card?.productMinStock?.totalBelowMin || 0} {i18n.t('dashboard.belowMin')}
                </span>
              </div>
            </div>

            {/* Card 2 */}
            <div className="card_dashboard summary-metric-card">
              <div className="card-icon-wrapper">
                <ShoppingCartIcon fontSize="large" className="card-icon" />
              </div>
              <h3>{i18n.t('generic.ordersToday')}</h3>
              <p className="card-main-value">{card?.totalOrders?.orderToday || 0}</p>
              <div className="card-subtext">
                <span className="week-orders">
                  {card?.totalOrders?.orderWeek || 0} {i18n.t('dashboard.thisWeek')}
                </span>
              </div>
            </div>

            {/* Card 3 */}
            <div className="card_dashboard summary-metric-card">
              <div className="card-icon-wrapper">
                <TrendingUpIcon fontSize="large" className="card-icon" />
              </div>
              <h3>{i18n.t('generic.mostOrdered')}</h3>
              <p className="card-main-value">{card?.mostWantedProduct?.name?.split(' ')[0] || i18n.t('dashboard.na')}</p>
              <div className="card-subtext">
                <span className="order-count">
                  {i18n.t('dashboard.ordersCount', {
                    count: card?.mostWantedProduct?.count || 0,
                  })}
                </span>
              </div>
            </div>

            <div className={`card_dashboard summary-metric-card server-health-card status-${(serverHealth?.status ?? 'checking').toLowerCase()}`}>
              <div className="card-icon-wrapper server-health-icon-wrapper">
                <DnsIcon fontSize="large" className="card-icon" />
              </div>
              <h3>{i18n.t('dashboard.serverHealth.title', { defaultValue: 'Server status' })}</h3>
              <p className="card-main-value server-health-value">
                {i18n.t(`dashboard.serverHealth.status.${(serverHealth?.status ?? 'Checking').toLowerCase()}`, {
                  defaultValue: serverHealth?.status ?? 'Checking',
                })}
              </p>
              <div className="server-health-metrics">
                <span>CPU <strong>{serverHealth?.cpuUsagePercent.toFixed(1) ?? '0.0'}%</strong></span>
                <span>{i18n.t('dashboard.serverHealth.memory', { defaultValue: 'Memory' })} <strong>{serverHealth?.memoryUsagePercent.toFixed(1) ?? '0.0'}%</strong></span>
              </div>
              <div className="card-subtext">
                <span className={`status-badge server-health-badge ${(serverHealth?.status ?? 'Checking').toLowerCase()}`}>
                  {serverHealth?.status === 'Offline'
                    ? i18n.t('dashboard.serverHealth.heartbeatLost', { defaultValue: 'Heartbeat lost' })
                    : i18n.t('dashboard.serverHealth.heartbeatActive', { defaultValue: 'Heartbeat active' })}
                </span>
              </div>
            </div>
            </div>
          </div>

          <WeatherCard
            latitude={companyLatitude}
            longitude={companyLongitude}
            locationName={companyLocationName}
          />
        </div>

        <div className="dashboard-spotlight">
          {pieChartData.map((item, index) => (
            <div key={index} className="spotlight-item">
              <div className="spotlight-head">
                <span className="spotlight-label">{item.category}</span>
                <span className="spotlight-value">
                  {item.total} {i18n.t('generic.orders')}
                </span>
              </div>
              <div className="spotlight-track">
                <span className="spotlight-fill" style={{ width: `${item.percentage}%`, ['--fill-color' as any]: item.color }} />
              </div>
              <span className="spotlight-percent">{item.percentage}%</span>
            </div>
          ))}
        </div>

        {/* CHARTS SECTION */}
        <div className="charts-section">
          <div className="section-header">
            <h2>{i18n.t('generic.monthlyOrdersAnalysis')}</h2>
            <span className="section-subtitle">{i18n.t('dashboard.performanceOverview', { year: new Date().getFullYear() })}</span>
          </div>

          <div className="charts-grid">
            {/* Restaurant chart */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>{i18n.t('generic.restaurantOrders')}</h3>
                <div className="chart-total">
                  <span className="total-value">{restaurantTotal}</span>
                  <span className="total-label">{i18n.t('dashboard.totalOrders')}</span>
                </div>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={animatedRestaurantData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#666', fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#666', fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      }}
                      formatter={(value: any) => [i18n.t('dashboard.ordersCount', { count: value }), i18n.t('generic.orders')]}
                    />
                    <Line
                      type="monotone"
                      dataKey="orders"
                      stroke="#ff4f78"
                      strokeWidth={3}
                      name={i18n.t('generic.restaurantOrders')}
                      dot={false}
                      activeDot={{ r: 5 }}
                      animationBegin={120}
                      animationDuration={1200}
                      animationEasing="ease-out"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Bar chart */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>{i18n.t('generic.barOrders')}</h3>
                <div className="chart-total">
                  <span className="total-value">{barTotal}</span>
                  <span className="total-label">{i18n.t('dashboard.totalOrders')}</span>
                </div>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={animatedBarData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#666', fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#666', fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      }}
                      formatter={(value: any) => [i18n.t('dashboard.ordersCount', { count: value }), i18n.t('generic.orders')]}
                    />
                    <Line
                      type="monotone"
                      dataKey="orders"
                      stroke="#2f8fe6"
                      strokeWidth={3}
                      name={i18n.t('generic.barOrders')}
                      dot={false}
                      activeDot={{ r: 5 }}
                      animationBegin={200}
                      animationDuration={1250}
                      animationEasing="ease-out"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Pie chart */}
            <div className="chart-card pie-chart-container">
              <div className="chart-header">
                <h3>{i18n.t('generic.orderDistribution')}</h3>
                <div className="chart-total">
                  <span className="total-value">{totalOrders}</span>
                </div>
              </div>
              <div className="pie-chart-wrapper">
                {pieChartData.length > 0 ? (
                  <div className="pie-chart-content">
                    <div className="pie-chart-graph">
                      <Pie
                        data={{
                          labels: pieChartData.map((item) => item.category),
                          datasets: [
                            {
                              data: pieChartData.map((item) => item.total),
                              backgroundColor: pieChartData.map((item) => item.color),
                              borderColor: '#ffffff',
                              borderWidth: 3,
                              hoverOffset: 8,
                            },
                          ],
                        }}
                        options={chartOptionsPie}
                      />
                      <div className="pie-center-label">
                        <span className="pie-center-value">{totalOrders}</span>
                        <span className="pie-center-text">{i18n.t('generic.orders')}</span>
                      </div>
                    </div>
                    <div className="pie-chart-legend">
                      {pieChartData
                        .filter((item) => item.total > 0)
                        .map((item, index) => (
                          <div key={index} className="legend-item" style={{ ['--legend-color' as any]: item.color }}>
                            <div className="legend-color" />
                            <div className="legend-details">
                              <span className="legend-label">{item.category}</span>
                              <span className="legend-value">
                                {item.total} {i18n.t('generic.orders')} ({item.percentage}%)
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div className="no-data-message">
                    <p>{i18n.t('generic.noDataAvailableForPieChart')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ORDERS SECTION */}
        <div className="orders-section">
          <div className="section-header">
            <h2>{i18n.t('generic.recentOrdered')}</h2>
            <span className="section-subtitle">{i18n.t('generic.ordersFromTheLast24Hours')}</span>
          </div>

          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>{i18n.t('generic.product')}</th>
                  <th>{i18n.t('generic.customer')}</th>
                  <th>{i18n.t('generic.status')}</th>
                  <th>{i18n.t('generic.quantity')}</th>
                  <th>{i18n.t('generic.orderDate')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map((order: any) => (
                    <tr key={order.id} className="order-row">
                      <td>
                        <div className="product-info">
                          <span className="product-name">{order.product?.name || i18n.t('dashboard.na')}</span>
                        </div>
                      </td>
                      <td>
                        <div className="user-info-dashboard">
                          <img
                            src={order.user?.avatar || 'https://via.placeholder.com/40'}
                            alt={order.user?.firstName || 'User'}
                            className="user-avatar"
                          />
                          <div className="user-details">
                            <span className="user-name">
                              {order.user ? `${order.user.firstName} ${order.user.lastName}` : i18n.t('dashboard.na')}
                            </span>
                            <span className="user-email">{order.user?.email || ''}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${order.statusDesc === 'Active' ? 'status-active' : 'status-complete'}`}>
                          {order.statusDesc}
                        </span>
                      </td>
                      <td>
                        <div className="quantity-cell">
                          <span className="quantity-value">{order.quantity}</span>
                        </div>
                      </td>
                      <td>
                        <div className="date-cell">
                          <span className="date-value">{format(new Date(order.created), 'dd/MM/yy')}</span>
                          <span className="time-value">{format(new Date(order.created), 'HH:mm')}</span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="no-orders-message">
                      <div className="empty-state">
                        <ShoppingCartIcon fontSize="large" />
                        <p>{i18n.t('generic.noOrdersFoundForTheLast24Hours')}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {isCashBalanceModalOpen && (
        <GeneralModal
          headerName={i18n.t('dashboard.cashBalance.modalTitle', { defaultValue: 'Register cashier opening balance' })}
          handleClose={() => {
            if (!isSavingCashBalance) {
              setIsCashBalanceModalOpen(false);
            }
          }}
          handleClick={saveCashBalance}
        >
          <div className="cash-balance-modal">
            <p className="cash-balance-modal-copy">
              {i18n.t('dashboard.cashBalance.modalCopy', {
                defaultValue: 'Insert the actual money currently available in the restaurant cashier to start the day.',
              })}
            </p>

            <label htmlFor="cashBalanceValue" className="cash-balance-label">
              {i18n.t('dashboard.cashBalance.valueLabel', { defaultValue: 'Cash balance value' })}
            </label>
            <input
              id="cashBalanceValue"
              type="number"
              min="0"
              step="0.01"
              className="cash-balance-input"
              value={cashBalanceValue}
              onChange={(e) => setCashBalanceValue(e.target.value)}
              disabled={isSavingCashBalance}
            />
          </div>
        </GeneralModal>
      )}
    </BasicCli>
  );
};

export default Dashboard;
